import { useState, useEffect, useCallback, useMemo } from 'react';
import Sidebar from '../components/Sidebar';

// --- Type Definitions ---

// 1. 고객 타입 (API 응답)
interface CustomerItem {
  customerIdx: number;
  groomName: string;
  brideName: string;
  status: string;
}

// 2. 직원 타입 (API 응답)
interface StaffItem {
  userIdx: number;
  name: string;
  status: string;
}

// 화면 표시용 일정 타입
interface Schedule {
  id: number;
  title: string;
  startDt: string;
  endDt: string;
  type: string;
  category: string;
  customerName: string;
  managerName: string;
  memo: string;
  customerIdx?: number;
  assignedStaffIdx?: number;
  roomIdx?: number;
}

// API 요청용 타입
interface ScheduleRequest {
  category: string;
  type: string;
  title: string;
  startDt: string;
  endDt: string;
  customerIdx: number;
  roomIdx?: number;
  assignedStaffIdx: number;
  memo: string;
}

// API 응답 아이템 타입 (유연한 타입)
interface ScheduleResponseItem {
  [key: string]: any; // snake_case 등 다양한 키 대응
  scheduleIdx: number;
  title: string;
  startDt: string;
  endDt: string;
  type: string;
  status: string | null;
  category: string;
  customerName: string;
  managerName: string;
  memo: string;
  customerIdx?: number;
  assignedStaffIdx?: number;
  roomIdx?: number;
}

interface ApiResponse<T> {
  status: boolean;
  code: string;
  message: string;
  data: T;
}

// ---------------------------

export default function CustomerSchedule() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<number | null>(new Date().getDate());
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState<Date | null>(new Date());
  
  // Data State
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);

  // 목록 데이터 State
  const [customers, setCustomers] = useState<CustomerItem[]>([]);
  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [customerSearchKeyword, setCustomerSearchKeyword] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  // 필터 State
  const [filterStaffIdx, setFilterStaffIdx] = useState<number | ''>('');

  // 폼 State
  const [scheduleForm, setScheduleForm] = useState({
    customerName: '',
    customerIdx: 0, // 0이면 직접 입력
    assignedStaffIdx: 0, 
    eventType: 'WEDDING',
    time: '10:00',
    venue: '',
    notes: ''
  });

  // --- Helpers ---
  const mapTypeToLabel = (apiType: string) => {
    switch (apiType) {
      case 'WEDDING': return '본식';
      case 'VENUE_VISIT': return '예식장 방문';
      case 'SDM': return '스드메';
      case 'ETC': return '기타';
      default: return apiType;
    }
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'WEDDING': return 'bg-pink-100 text-pink-800';
      case 'VENUE_VISIT': return 'bg-blue-100 text-blue-800';
      case 'SDM': return 'bg-purple-100 text-purple-800';
      case 'ETC': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCustomerName = (customer: CustomerItem) => `${customer.groomName} & ${customer.brideName}`;

  const filteredCustomers = useMemo(() => {
    const keyword = customerSearchKeyword.trim().toLowerCase();
    if (!keyword) return customers;

    return customers.filter((customer) => {
      const fullName = `${customer.groomName} ${customer.brideName}`.toLowerCase();
      return (
        fullName.includes(keyword) ||
        customer.groomName.toLowerCase().includes(keyword) ||
        customer.brideName.toLowerCase().includes(keyword)
      );
    });
  }, [customers, customerSearchKeyword]);

  // --- API Functions ---

  // 1. 고객 목록 조회
  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/customer?size=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status && result.data?.items) {
        setCustomers(result.data.items);
      }
    } catch (e) {
      console.error("고객 목록 조회 실패", e);
    }
  };

  // 2. 직원 목록 조회
  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status && result.data?.items) {
        setStaffList(result.data.items);
      }
    } catch (e) {
      console.error("직원 목록 조회 실패", e);
    }
  };

  // 3. 일정 조회 (로그 추가)
  const fetchSchedules = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const fromDt = firstDay.toISOString().split('T')[0];
      const toDt = lastDay.toISOString().split('T')[0];
      const token = localStorage.getItem('accessToken');

      let url = `https://api.wedmin.biz/schedule/calendar?fromDt=${fromDt}&toDt=${toDt}&category=CUSTOMER_EVENT`;
      if (filterStaffIdx) {
        url += `&assignedStaffIdx=${filterStaffIdx}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('일정 조회 실패');
      const json: ApiResponse<ScheduleResponseItem[]> = await response.json();
      
      if (json.status && json.data) {
        console.log("🔥 [Fetch] API Raw Data (Sample):", json.data[0]);

        const mappedData: Schedule[] = json.data.map(item => {
          // 다양한 케이스 대응
          const customerIdx = item.customerIdx || item.customer_idx || 0;
          const assignedStaffIdx = item.assignedStaffIdx || item.assigned_staff_idx || item.managerIdx || 0;

          return {
            id: item.scheduleIdx,
            title: item.title,
            startDt: item.startDt,
            endDt: item.endDt,
            type: item.type,
            category: item.category,
            customerName: item.customerName,
            managerName: item.managerName,
            memo: item.memo,
            customerIdx: Number(customerIdx),
            assignedStaffIdx: Number(assignedStaffIdx),
            roomIdx: item.roomIdx,
          };
        });
        setSchedules(mappedData);
      }
    } catch (error) {
      console.error("Fetch schedules error:", error);
    }
  }, [currentDate, filterStaffIdx]);

  // 4. 일정 등록
  const createSchedule = async (data: ScheduleRequest) => {
    console.log("🔥 [Submit] Create Request:", data);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('등록 실패');
      const result = await response.json();
      
      if (result.status) {
        alert("일정이 등록되었습니다.");
        setShowScheduleModal(false);
        resetForm();
        fetchSchedules();
      } else {
        alert(`등록 실패: ${result.message}`);
      }
    } catch (error) {
      console.error(error);
      alert("오류가 발생했습니다.");
    }
  };

  // 5. 일정 수정
  const updateSchedule = async (scheduleIdx: number, data: ScheduleRequest) => {
    console.log("🔥 [Submit] Update Request:", data);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/schedule/${scheduleIdx}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('수정 실패');
      const result = await response.json();
      
      if (result.status) {
        alert("일정이 수정되었습니다.");
        setShowScheduleModal(false);
        resetForm();
        fetchSchedules();
      } else {
        alert(`수정 실패: ${result.message}`);
      }
    } catch (error) {
      console.error(error);
      alert("오류가 발생했습니다.");
    }
  };

  // 6. 일정 삭제
  const deleteSchedule = async (scheduleIdx: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/schedule/${scheduleIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('삭제 실패');
      const result = await response.json();
      
      if (result.status) {
        alert("삭제되었습니다.");
        if (showScheduleModal) {
            setShowScheduleModal(false);
            resetForm();
        }
        fetchSchedules();
      } else {
        alert(`삭제 실패: ${result.message}`);
      }
    } catch (error) {
      console.error(error);
      alert("오류가 발생했습니다.");
    }
  };

  // --- Effects ---
  useEffect(() => {
    fetchCustomers();
    fetchStaff();
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);


  // --- UI Logic ---

  const resetForm = () => {
    setScheduleForm({
      customerName: '',
      customerIdx: 0,
      assignedStaffIdx: 0,
      eventType: 'WEDDING',
      time: '10:00',
      venue: '',
      notes: ''
    });
    setCustomerSearchKeyword('');
    setIsCustomerDropdownOpen(false);
    setSelectedSchedule(null);
  };

  // ✅ [복구됨] 달력 계산 로직
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const prevMonth = new Date(year, month - 1, 0);
    
    // 이전 달 채우기
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonth.getDate() - i,
        isCurrentMonth: false,
        isToday: false,
      });
    }

    // 현재 달 채우기
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = new Date().getDate() === day && 
                     new Date().getMonth() === month && 
                     new Date().getFullYear() === year;
      days.push({
        day,
        isCurrentMonth: true,
        isToday,
      });
    }

    // 다음 달 채우기 (총 42칸 유지)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        isToday: false,
      });
    }
    return days;
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  const goToPreviousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  const handleDateClick = (dayInfo: any) => {
    if (dayInfo.isCurrentMonth) {
      setSelectedDate(dayInfo.day);
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayInfo.day);
      setSelectedScheduleDate(date);
      resetForm();
      setShowScheduleModal(true);
    }
  };

  // ✅ [Click] 일정 클릭 핸들러 (로그 추가)
  const handleScheduleClick = (e: React.MouseEvent, schedule: Schedule) => {
    e.stopPropagation();
    console.log("🔥 [Click] Selected Schedule:", schedule); // 클릭한 스케줄 데이터 확인

    setSelectedSchedule(schedule);
    setSelectedDate(new Date(schedule.startDt).getDate());
    setSelectedScheduleDate(new Date(schedule.startDt));
    
    const start = new Date(schedule.startDt);
    const startTimeStr = start.toTimeString().substring(0, 5);

    // 폼 State 업데이트
    setScheduleForm({
      customerName: schedule.customerName,
      customerIdx: schedule.customerIdx || 0,
      assignedStaffIdx: schedule.assignedStaffIdx || 0,
      eventType: schedule.type,
      time: startTimeStr,
      venue: '', 
      notes: schedule.memo
    });
    setCustomerSearchKeyword(schedule.customerName || '');
    setIsCustomerDropdownOpen(false);
    
    setShowScheduleModal(true);
  };

  // ✅ [Submit] 등록/수정 핸들러 (로그 추가)
  const handleScheduleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScheduleDate) return;

    if (!scheduleForm.customerName) {
      alert("고객명을 입력해주세요.");
      return;
    }

    // 날짜 + 시간 조합
    const y = selectedScheduleDate.getFullYear();
    const m = String(selectedScheduleDate.getMonth() + 1).padStart(2, '0');
    const d = String(selectedScheduleDate.getDate()).padStart(2, '0');
    const startDt = `${y}-${m}-${d}T${scheduleForm.time}:00`;
    
    // 종료 시간 (+1시간)
    const startDateObj = new Date(startDt);
    startDateObj.setHours(startDateObj.getHours() + 1);
    
    // ISO String 변환
    const endY = startDateObj.getFullYear();
    const endM = String(startDateObj.getMonth() + 1).padStart(2, '0');
    const endD = String(startDateObj.getDate()).padStart(2, '0');
    const endH = String(startDateObj.getHours()).padStart(2, '0');
    const endMin = String(startDateObj.getMinutes()).padStart(2, '0');
    const endDt = `${endY}-${endM}-${endD}T${endH}:${endMin}:00`;

    const requestBody: ScheduleRequest = {
      category: 'CUSTOMER_EVENT',
      type: scheduleForm.eventType,
      title: `${scheduleForm.customerName}}`,
      startDt: startDt,
      endDt: endDt,
      customerIdx: scheduleForm.customerIdx,
      assignedStaffIdx: scheduleForm.assignedStaffIdx,
      roomIdx: 0,
      memo: scheduleForm.notes
    };

    if (selectedSchedule) {
      updateSchedule(selectedSchedule.id, requestBody);
    } else {
      createSchedule(requestBody);
    }
  };

  // 날짜별 일정 필터링
  const getSchedulesForDay = (day: number, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) return [];
    return schedules.filter(s => {
      const sDate = new Date(s.startDt);
      return sDate.getDate() === day && 
             sDate.getMonth() === currentDate.getMonth() && 
             sDate.getFullYear() === currentDate.getFullYear();
    });
  };

  const handleCustomerSearchChange = (value: string) => {
    setCustomerSearchKeyword(value);
    setIsCustomerDropdownOpen(true);
    setScheduleForm((prev) => ({
      ...prev,
      customerIdx: 0,
      customerName: value,
    }));
  };

  const handleSelectCustomer = (customer: CustomerItem) => {
    const name = formatCustomerName(customer);
    setScheduleForm((prev) => ({
      ...prev,
      customerIdx: customer.customerIdx,
      customerName: name,
    }));
    setCustomerSearchKeyword(name);
    setIsCustomerDropdownOpen(false);
  };

  const handleManualCustomer = () => {
    setScheduleForm((prev) => ({
      ...prev,
      customerIdx: 0,
      customerName: customerSearchKeyword.trim(),
    }));
    setIsCustomerDropdownOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">고객 일정 관리</h1>
            <p className="text-gray-600">고객의 예식 일정과 중요 이벤트를 관리하세요</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* 담당자 필터 */}
            <select
              value={filterStaffIdx}
              onChange={(e) => setFilterStaffIdx(e.target.value ? Number(e.target.value) : '')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer"
            >
              <option value="">전체 담당자</option>
              {staffList.map(staff => (
                <option key={staff.userIdx} value={staff.userIdx}>{staff.name}</option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 text-sm bg-white border rounded-lg hover:bg-gray-50">오늘</button>
              <div className="flex items-center bg-white border rounded-lg">
                <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-50"><i className="ri-arrow-left-s-line"></i></button>
                <button onClick={goToNextMonth} className="p-2 hover:bg-gray-50"><i className="ri-arrow-right-s-line"></i></button>
              </div>
            </div>
            
            <button 
              onClick={() => {
                resetForm();
                setSelectedScheduleDate(new Date());
                setShowScheduleModal(true);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + 일정 등록
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{currentDate.getFullYear()}년 {monthNames[currentDate.getMonth()]}</h2>
          </div>
          <div className="grid grid-cols-7 border-b border-gray-200">
            {dayNames.map((dayName, index) => (
              <div key={dayName} className={`p-4 text-center text-sm font-medium ${index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-700'}`}>{dayName}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((dayInfo, index) => {
              const isSelected = selectedDate === dayInfo.day && dayInfo.isCurrentMonth;
              const daySchedules = getSchedulesForDay(dayInfo.day, dayInfo.isCurrentMonth);
              return (
                <div 
                  key={index}
                  className="min-h-[120px] border-r border-b border-gray-100 p-2 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleDateClick(dayInfo)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-sm font-medium ${!dayInfo.isCurrentMonth ? 'text-gray-300' : dayInfo.isToday ? 'text-white bg-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs' : isSelected ? 'text-white bg-blue-500 rounded-full w-6 h-6 flex items-center justify-center text-xs' : index % 7 === 0 ? 'text-red-500' : index % 7 === 6 ? 'text-blue-500' : 'text-gray-900'}`}>{dayInfo.day}</span>
                  </div>
                  {dayInfo.isCurrentMonth && (
                    <div className="space-y-1">
                      {daySchedules.map(schedule => (
                        <div key={schedule.id} onClick={(e) => handleScheduleClick(e, schedule)} className={`${getStatusColor(schedule.type)} text-xs px-2 py-1 rounded truncate cursor-pointer hover:opacity-80`}>
                          {schedule.title}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-pink-100 rounded"></div><span>본식</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-100 rounded"></div><span>예식장 방문</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-purple-100 rounded"></div><span>스드메</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-100 rounded"></div><span>기타</span></div>
          </div>
        </div>
      </div>

      {/* 일정 등록/수정 모달 */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">{selectedSchedule ? '일정 수정' : '일정 등록'}</h3>
                <button onClick={() => setShowScheduleModal(false)} className="text-gray-400 hover:text-gray-600"><i className="ri-close-line text-2xl"></i></button>
              </div>
              {selectedScheduleDate && <p className="text-sm text-gray-600 mt-2">{selectedScheduleDate.getFullYear()}년 {selectedScheduleDate.getMonth() + 1}월 {selectedScheduleDate.getDate()}일</p>}
            </div>

            <form onSubmit={handleScheduleSubmit} className="p-6">
              <div className="space-y-4">
                
                {/* ✅ 고객 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">고객 선택 <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      required
                      type="text"
                      value={customerSearchKeyword}
                      onChange={(e) => handleCustomerSearchChange(e.target.value)}
                      onFocus={() => setIsCustomerDropdownOpen(true)}
                      onBlur={() => {
                        window.setTimeout(() => setIsCustomerDropdownOpen(false), 120);
                      }}
                      placeholder="고객명을 입력해 검색하거나 직접 입력하세요"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    {isCustomerDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-auto border border-gray-200 rounded-lg bg-white shadow-lg z-20">
                        {customerSearchKeyword.trim() && (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleManualCustomer();
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100"
                          >
                            직접 입력 (비회원): {customerSearchKeyword.trim()}
                          </button>
                        )}
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map((customer) => (
                            <button
                              key={customer.customerIdx}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelectCustomer(customer);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50"
                            >
                              {formatCustomerName(customer)}
                            </button>
                          ))
                        ) : (
                          <p className="px-4 py-2 text-sm text-gray-500">검색 결과가 없습니다.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {scheduleForm.customerIdx > 0 ? '등록 고객 선택 완료' : '비회원은 이름을 입력한 뒤 직접 입력 항목을 선택하세요.'}
                  </p>
                </div>

                {/* ✅ 담당자 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">담당자</label>
                  <select
                    value={scheduleForm.assignedStaffIdx}
                    onChange={(e) => {
                      const idx = Number(e.target.value);
                      console.log("🔥 [Change] Staff:", idx);
                      setScheduleForm({...scheduleForm, assignedStaffIdx: idx});
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value={0}>미지정</option>
                    {staffList.map(s => (
                      <option key={s.userIdx} value={s.userIdx}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">일정 유형</label>
                  <select
                    value={scheduleForm.eventType}
                    onChange={(e) => setScheduleForm({...scheduleForm, eventType: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="WEDDING">본식</option>
                    <option value="REHEARSAL">리허설</option>
                    <option value="VENUE_VISIT">예식장 방문</option>
                    <option value="SDM">스드메</option>
                    <option value="ETC">기타</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">시간</label>
                  <input
                    type="time"
                    value={scheduleForm.time}
                    onChange={(e) => setScheduleForm({...scheduleForm, time: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">메모</label>
                  <textarea
                    value={scheduleForm.notes}
                    onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                {selectedSchedule && (
                  <button type="button" onClick={() => deleteSchedule(selectedSchedule.id)} className="flex-1 px-4 py-2 bg-red-100 text-red-600 rounded-lg">삭제</button>
                )}
                <button type="button" onClick={() => setShowScheduleModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">취소</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg">{selectedSchedule ? '수정' : '등록'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
